import { isPromise, awaited, type TPromiseOptions } from '@epihub/core/src/fn/promise';
import { promiseRetry, type TPromiseRetryOptions } from '@epihub/core/src/fn/promiseRetry';
import { normalizeError } from '@epihub/core/src/errors';

export function safely<T, ErrRet = undefined>(
  run: () => T,
  errFn?: (err: unknown) => ErrRet,
): T | ErrRet {
  try {
    return run();
  } catch (e) {
    return errFn
      ? errFn(e)
      : undefined as ErrRet;
  }
}

export async function safelyWait<T, ErrRet = undefined>(
  run: () => Promise<T>,
  errFn?: (err: unknown) => ErrRet | Promise<ErrRet>,
): Promise<T | ErrRet> {
  try {
    return await run();
  } catch (e) {
    return errFn
      ? await errFn(e)
      : undefined as ErrRet;
  }
}

export function safelyDetach(
  run: () => Promise<any> | any,
  options?: TPromiseOptions & {
    retry?: TPromiseRetryOptions<void>;
  },
) {
  async function promisifyRun() {
    return awaited(async () => {
      // Run the callback, it may not return a thenable
      const result = run();

      // If it returns a promise, wait
      // this can be aborted through the abort signal in options
      if (isPromise(result)) {
        await result;
      }
    }, options);
  }

  try {
    const retryable = options?.retry;
    const callbackToUse = retryable
      ? () => promiseRetry(async () => promisifyRun(), retryable)
      : () => promisifyRun();

    callbackToUse()
      .catch((e) => normalizeError(e).log());
  } catch (e) {
    normalizeError(e).log();
  }
}
